import React from "react";
import { object, shape, string, any, elementType, arrayOf, bool } from "prop-types";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import Section from "@insightfulscience/atomic-react/Section";
import ContentContainer from "@insightfulscience/atomic-react/r1/atoms/ContentContainer";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import Image from "@insightfulscience/atomic-react/Image";
import WistiaVideo from "@insightfulscience/atomic-react/WistiaVideo";
import WistiaVideoBlockDetail, {
	WistiaVideoBlockDetailFrame,
	WistiaVideoBlockDetailInfo,
	WistiaVideoBlockDetailInfoItem,
} from "@insightfulscience/atomic-react/WistiaVideo/BlockDetail";
import { useLocale } from "@insightfulscience/smart-react/i18";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import Link from "@insightfulscience/atomic-react/Link";
import WistiaVideoEmbed from "@insightfulscience/atomic-react/WistiaVideoEmbed";
import { BRAND_NAMES } from "../../constants";
import SectionHero from "./components/SectionHero";
import RecommendedVideos from "../components/RecommendedVideos";
import renderBlockByType from "../../utils/renderBlockByType";
import BackLink from "../components/BackLink/BackLink";
import { removeDrafts } from "../../utils/removeDrafts";
import AlertPreviewMode from "../../ui/AlertPreviewMode";

const SeriesPage = ({ isPreview, video, pageProps }) => {
	const [t] = useLocale("series");
	const {
		siteDomain,
		brandCode,
		backLink,
		showLogoAcademy = false,
		academyToCComponent = null,
		showToC = false,
	} = pageProps;
	const {
		title,
		metaTitle = "",
		metaDescription = "",
		subtitle,
		slug,
		videos,
		thumbnail,
		recommendedVideos = [],
		additionalResources = [],
		academy = false,
	} = video;
	const brandName = BRAND_NAMES[brandCode];
	const thumbnailUrl = thumbnail?.fields?.file?.url || "";

	const filteredRecommendedVideos = recommendedVideos?.filter(removeDrafts) || [];

	const filteredVideos = videos?.filter(removeDrafts) || [];

	let tocItems = filteredVideos.map(item => ({
		title: item.fields.title,
		slug: item.sys.id,
	}));

	tocItems =
		academyToCComponent?.items?.length > 0 ? [...tocItems, ...academyToCComponent.items] : tocItems;

	return (
		<>
			<PageMeta title={`${metaTitle} - ${brandName}`} description={metaDescription}>
				<PageMeta.OGP
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					siteName={t("META_SITE_NAME")}
					title={`${metaTitle} - ${brandName}`}
					description={metaDescription}
					url={`${siteDomain}/series/${slug}`}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					image={thumbnailUrl || t("META_IMAGE")}
					imageSecureUrl={thumbnailUrl || t("META_IMAGE_SECURE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Twitter
					card={t("META_TWITTER_CARD")}
					site={t("META_TWITTER_SITE")}
					creator={t("META_TWITTER_CREATOR")}
					title={`${metaTitle} - ${brandName}`}
					description={metaDescription}
					image={thumbnailUrl || t("META_TWITTER_IMAGE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Canonical href={`${siteDomain}/series/${slug}`} />
			</PageMeta>
			{isPreview ? <AlertPreviewMode /> : null}
			<SectionHero brandCode={brandCode}>
				{(academy || showLogoAcademy) && (
					<HeroRow center>
						<HeroCol lg={5} md={5} sm={5} spacing={{ mb: 6 }}>
							<Link href={t("ACADEMY_LINK")}>
								<Image src={t("ACADEMY_LOGO")} isBaseUrl />
							</Link>
						</HeroCol>
					</HeroRow>
				)}
				{!(academy || showLogoAcademy) && backLink && <BackLink backLink={backLink} />}
				<HeroTitle>{title}</HeroTitle>
				<HeroDescription>{subtitle}</HeroDescription>
			</SectionHero>
			{(academy || showToC) &&
				tocItems.length > 1 &&
				(academyToCComponent.Component ? <academyToCComponent.Component items={tocItems} /> : null)}
			<Section spacing={{ mt: 14, py: 8 }}>
				<ContentContainer lg={10} center>
					{filteredVideos.length > 0 && (
						<WistiaVideo list={filteredVideos}>
							{filteredVideos.map(item => {
								const { fields, sys } = item;

								return (
									<WistiaVideoBlockDetail
										key={`${fields.title}_${sys.id}`}
										id={sys.id}
										dataId={sys.id}
									>
										<WistiaVideoBlockDetailFrame dataId={sys.id}>
											<WistiaVideoEmbed url={fields.videoUrl} />
										</WistiaVideoBlockDetailFrame>
										<WistiaVideoBlockDetailInfo title={fields.title}>
											{fields?.description ? (
												<WistiaVideoBlockDetailInfoItem title={t("VIDEO_SUBTITLE_TEXT")}>
													{fields.description}
												</WistiaVideoBlockDetailInfoItem>
											) : null}
											{fields?.lengthString ? (
												<WistiaVideoBlockDetailInfoItem title={t("VIDEO_SUBTITLE_LENGTH")}>
													{fields.lengthString}
												</WistiaVideoBlockDetailInfoItem>
											) : null}
										</WistiaVideoBlockDetailInfo>
									</WistiaVideoBlockDetail>
								);
							})}
						</WistiaVideo>
					)}
				</ContentContainer>
			</Section>
			{filteredRecommendedVideos.length > 0 && (
				<RecommendedVideos brandCode={brandCode} videos={filteredRecommendedVideos} />
			)}
			{additionalResources.map(renderBlockByType(slug))}
		</>
	);
};

SeriesPage.propTypes = {
	isPreview: bool,
	video: shape({
		title: string.isRequired,
		subtitle: string.isRequired,
		metaDescription: string,
		slug: string.isRequired,
		body: object, // eslint-disable-line react/forbid-prop-types,
		toCLink: string,
	}),
	pageProps: shape({
		resources: any, // eslint-disable-line react/forbid-prop-types,
		siteDomain: string,
		brandCode: string,
		backLink: shape({
			title: string.isRequired,
			href: string.isRequired,
		}),
		showToC: bool,
		academyToCComponent: shape({
			Component: elementType,
			items: arrayOf(
				shape({
					title: string.isRequired,
					slug: string.isRequired,
				}),
			),
		}),
	}),
};

export default SeriesPage;
